import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getFromLocalStorage,
  setOnLocalStorage,
  StorageKey,
} from '../../helpers/localstorage';
import { useLanguage } from '../../locale/useLanguage';
import { authTokenSelector } from '../../redux/user/user.selectors';
import { Routes } from '../../routes/types';
import { Button } from '../Button/Button';
import { Modal, ModalRef } from '../Modal/Modal';

import './SignUpModal.css';

type SignUpModalProps = {};

export const SignUpModal: React.ForwardRefExoticComponent<
  SignUpModalProps & React.RefAttributes<ModalRef>
> = forwardRef((_, forwardedRef) => {
  const [t] = useLanguage();
  const history = useHistory();
  const token = useSelector(authTokenSelector);
  const modalRef = useRef<ModalRef>(null);

  useImperativeHandle(forwardedRef, () => ({
    openModal: modalRef.current!.openModal,
    closeModal: modalRef.current!.closeModal,
  }));

  useEffect(() => {
    const isAlreadyAsked = getFromLocalStorage(StorageKey.IsSignInModalAsked);
    if (!isAlreadyAsked && !token) {
      const timeoutRef = setTimeout(() => {
        setOnLocalStorage('true', StorageKey.IsSignInModalAsked);
        modalRef.current?.openModal();
      }, 20000);

      return () => clearTimeout(timeoutRef);
    }
  }, [token]);

  const goToSignIn = useCallback(() => {
    history.push(Routes.SignIn);
  }, [history]);

  const goToSignUp = useCallback(() => {
    history.push(Routes.SignUp);
  }, [history]);

  return (
    <Modal ref={modalRef}>
      <h3 className="modal-title">{t('requestSignUpModal.title')}</h3>
      <p className="modal-description">{t('requestSignUpModal.description')}</p>
      <div className="modal-button-container">
        <Button
          isPrimary
          onClick={goToSignUp}
          label={t('requestSignUpModal.signUp')}
        />
        <div className="sign-up-modal--sign-in-button">
          <Button onClick={goToSignIn} label={t('requestSignUpModal.signIn')} />
        </div>
      </div>
    </Modal>
  );
});
